import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  LayoutDashboard, Activity, Bot, Sword, Users, Shield, Newspaper, Coins,
  Crown, ScrollText, BarChart3, LogOut, Menu, X, ChevronLeft, ChevronRight,
  ShieldCheck, ArrowLeft, CreditCard, KeyRound, MessageCircle, HandCoins,
  Download, Link2, Wrench, Users2, Mail, Sparkles, Receipt, Tv, ShoppingCart,
  CalendarRange, Megaphone,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { BRAND_NAME, PANEL_LOGIN_PATH } from '@/shared/config/flavor';
import { GmGate } from './GmGate';
import { usePushSubscribe } from '@/shared/hooks/usePushSubscribe';
import { AlertBell } from '@/features/Notifications';

/** "Instalar app" (dono 2026-10-20): o painel vira app no celular/PC (o push do GM no iPhone exige o site instalado).
 *  Só aparece quando o navegador oferece a instalação (Chrome/Edge/Android) e o site ainda não está instalado. */
function InstallAppButton() {
  const [prompt, setPrompt] = useState<any>(() => (window as any).__nextInstallPrompt || null);
  useEffect(() => {
    const ready = () => setPrompt((window as any).__nextInstallPrompt || null);
    const installed = () => setPrompt(null);
    window.addEventListener('next-install-ready', ready);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('next-install-ready', ready);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);
  if (!prompt || window.matchMedia('(display-mode: standalone)').matches) return null;
  return (
    <button
      onClick={async () => {
        prompt.prompt();
        try { await prompt.userChoice; } catch { /* fechou */ }
        (window as any).__nextInstallPrompt = null;
        setPrompt(null);
      }}
      title="Instalar o painel como app (notificações no celular)"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold cursor-pointer
                 bg-[#00e1d7]/10 border border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20 transition"
    >
      <Download size={13} />
      <span className="hidden sm:inline">Instalar app</span>
    </button>
  );
}

interface NavItemDef {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  feature?: string;     // se setado, item so aparece se hasFeature(feature)
}

const NAV_ITEMS: NavItemDef[] = [
  { to: '/admin',            label: 'Dashboard',     icon: <LayoutDashboard size={18} />, end: true, feature: 'dashboard' },
  { to: '/admin/onlines',    label: 'Online',        icon: <Activity size={18} />,        feature: 'accounts_view' },
  { to: '/admin/characters', label: 'Personagens',   icon: <Sword size={18} />,           feature: 'characters_view' },
  { to: '/admin/accounts',   label: 'Contas',        icon: <Users size={18} />,           feature: 'accounts_view' },
  { to: '/admin/guilds',     label: 'Guilds',        icon: <Users2 size={18} />,          feature: 'guilds_view' },
  { to: '/admin/emails',     label: 'Emails',        icon: <Mail size={18} />,            feature: 'mail' },
  { to: '/admin/guard',      label: 'Guard',         icon: <Shield size={18} />,          feature: 'ban' },
  { to: '/admin/bots',       label: 'Bots',          icon: <Bot size={18} />,             feature: 'bots_manage' },
  { to: '/admin/gm-tools',   label: 'Ferramentas',   icon: <Wrench size={18} />,          feature: 'gm_tools' },
  { to: '/admin/tickets',    label: 'Tickets',       icon: <MessageCircle size={18} />,   feature: 'tickets' },
  { to: '/admin/gamelog',    label: 'GameLog',       icon: <BarChart3 size={18} />,       feature: 'monitor' },
  { to: '/admin/news',       label: 'Notícias',      icon: <Newspaper size={18} />,       feature: 'news_crud' },
  { to: '/admin/marketing',  label: 'Marketing',     icon: <Megaphone size={18} />,       feature: 'marketing' },
  { to: '/admin/events',     label: 'Eventos',       icon: <CalendarRange size={18} />,   feature: 'events_manage' },
  { to: '/admin/streamers',  label: 'Streamers',     icon: <Tv size={18} />,              feature: 'streamers_manage' },
  { to: '/admin/referrals',  label: 'Referências',   icon: <Link2 size={18} />,           feature: 'referrals_view' },
  { to: '/admin/cashshop',   label: 'Cash Shop',     icon: <ShoppingCart size={18} />,    feature: 'recharges_view' },
  { to: '/admin/recharges',  label: 'Recargas',      icon: <Coins size={18} />,           feature: 'recharges_view' },
  { to: '/admin/services',   label: 'Serviços',      icon: <Sparkles size={18} />,        feature: 'recharges_view' },
  { to: '/admin/sales',      label: 'Vendas',        icon: <Receipt size={18} />,         feature: 'recharges_view' },
  { to: '/admin/premium',    label: 'Premium',       icon: <Crown size={18} />,           feature: 'premium_manage' },
  { to: '/admin/logs',       label: 'GM Logs',       icon: <ScrollText size={18} />,      feature: 'gm_logs' },
  { to: '/admin/gateways',   label: 'Gateways',      icon: <CreditCard size={18} />,      feature: 'payment_gateways' },
  { to: '/admin/access',     label: 'Acessos',       icon: <KeyRound size={18} />,        feature: 'access_manage' },
  { to: '/admin/rmt',        label: 'RMT',           icon: <HandCoins size={18} />,       feature: 'rmt_manage' },
  { to: '/admin/client-download', label: 'Download',  icon: <Download size={18} />,       feature: 'client_download' },
];

const COLLAPSE_KEY = 'liberty_admin_sidebar_collapsed';

export default function AdminLayout() {
  const { user, account, logout, isLoggedIn, hasFeature, gmInfo } = useAuth();
  const navItems = NAV_ITEMS.filter(it => !it.feature || hasFeature(it.feature));

  // Web Push: ao entrar no painel admin, se o usuario for GM, pede permissao
  // de notificacao e cria/atualiza a subscription (kind=1=gm).
  const isGm = !!gmInfo && gmInfo.gmLevel >= 1;
  usePushSubscribe({
    enabled: isLoggedIn && isGm,
    userNum: account?.userNum ?? null,
    kind: 1,
  });
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (!isLoggedIn) navigate(PANEL_LOGIN_PATH, { replace: true });
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  const handleLogout = () => { logout(); navigate(PANEL_LOGIN_PATH); };

  if (!isLoggedIn) return null;

  // forceExpanded = gaveta do celular: a gaveta inteira rola (sem rolagem escondida so' na lista) e os itens ficam
  // em 2 colunas -- os 26 menus cabem quase numa tela (dono 2026-10-21: "nao consigo ver todos menus" no celular)
  const renderNav = (forceExpanded = false) => {
    const isCollapsed = collapsed && !forceExpanded;
    const mobile = forceExpanded;
    return (
      <div className={`flex flex-col ${mobile ? 'min-h-full pt-3' : 'h-full pt-4'}`}>
        {/* Brand */}
        <div className={`px-4 pb-4 mb-2 border-b border-white/[0.05] ${isCollapsed ? 'flex justify-center' : ''}`}>
          {isCollapsed ? (
            <div className="w-10 h-10 rounded-xl bg-[#00e1d7]/10 border border-[#00e1d7]/30 flex items-center justify-center">
              <ShieldCheck size={18} className="text-[#00e1d7]" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#00e1d7]/10 border border-[#00e1d7]/30 flex items-center justify-center">
                <ShieldCheck size={18} className="text-[#00e1d7]" />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.15em] text-white leading-tight">
                  Painel GM
                </p>
                <p className="text-[10px] text-white/45 mt-0.5">{BRAND_NAME} Admin</p>
              </div>
            </div>
          )}
        </div>

        <nav className={mobile
          ? 'grid grid-cols-2 gap-1 px-2 pb-2'
          : `flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden ${isCollapsed ? 'px-2' : 'px-0'}`}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) => mobile
                ? `flex items-center gap-2 min-w-0 px-3 py-2.5 rounded-md border transition-colors
                   ${isActive
                     ? 'bg-[#00e1d7]/10 text-[#00e1d7] border-[#00e1d7]/40'
                     : 'text-white/75 border-white/[0.06] bg-white/[0.02] active:bg-white/[0.06]'}`
                : `relative flex items-center rounded-none transition-all duration-200 group
                ${isCollapsed ? 'justify-center h-10 w-10 mx-auto' : 'gap-3 px-5 py-2'}
                ${isActive
                  ? 'bg-[#00e1d7]/10 text-[#00e1d7]' + (isCollapsed ? '' : ' border-l-2 border-[#00e1d7]')
                  : 'text-white/65 hover:bg-white/[0.04] hover:text-white' + (isCollapsed ? '' : ' border-l-2 border-transparent')
                }`
              }
            >
              <span className="opacity-90 shrink-0">{item.icon}</span>
              {!isCollapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
              {isCollapsed && (
                <span className="absolute left-full ml-3 px-2 py-1 rounded-md text-xs font-medium
                                 bg-[#161e26] border border-white/[0.08] text-white whitespace-nowrap
                                 opacity-0 group-hover:opacity-100 pointer-events-none
                                 transition-opacity duration-200 z-50 shadow-lg">
                  {item.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Voltar pro user dashboard */}
        <div className={`border-t border-white/[0.05] ${isCollapsed ? 'p-2' : 'p-3'} space-y-1`}>
          <Link
            to="/dashboard"
            title={isCollapsed ? 'Voltar' : undefined}
            className={`relative flex items-center rounded-lg text-sm group
                       text-white/55 hover:text-white hover:bg-white/[0.04]
                       transition-all duration-200 cursor-pointer
                       ${isCollapsed ? 'justify-center h-10 w-10 mx-auto' : 'gap-3 w-full px-3.5 py-2'}`}
          >
            <ArrowLeft size={16} className="shrink-0" />
            {!isCollapsed && <span>Voltar ao site</span>}
          </Link>
          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Sair' : undefined}
            className={`relative flex items-center rounded-lg text-sm group
                       text-white/55 hover:text-red-400 hover:bg-red-500/10
                       transition-all duration-200 cursor-pointer
                       ${isCollapsed ? 'justify-center h-10 w-10 mx-auto' : 'gap-3 w-full px-3.5 py-2'}`}
          >
            <LogOut size={16} className="shrink-0" />
            {!isCollapsed && <span>Sair</span>}
          </button>
        </div>
      </div>
    );
  };

  const sidebarWidth = collapsed ? 'md:w-[64px]' : 'md:w-60';
  const mainPadding = collapsed ? 'md:ml-[64px]' : 'md:ml-60';

  return (
    <GmGate>
      <div className="admin-square min-h-screen flex relative text-white surface-app-bg">
        {/* Topbar */}
        <header className={`fixed top-0 right-0 left-0 h-14 bg-[#161e26]
                           border-b border-white/[0.06] flex items-center justify-between
                           px-4 md:px-6 z-50 transition-[left] duration-300
                           ${collapsed ? 'md:left-[64px]' : 'md:left-60'}`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="text-white cursor-pointer md:hidden"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <span className="text-xs font-black uppercase tracking-[0.15em] text-white">
              {BRAND_NAME} · GM
            </span>
          </div>
          <div className="flex items-center gap-3">
            <InstallAppButton />
            <AlertBell />
            {gmInfo && (
              <span className={`text-[10px] font-black uppercase tracking-[0.15em]
                                px-2 py-0.5 rounded
                                ${gmInfo.isSuper
                                  ? 'bg-[#00e1d7]/15 text-[#00e1d7] border border-[#00e1d7]/40'
                                  : 'bg-white/[0.06] text-white/70 border border-white/10'}`}>
                {gmInfo.isSuper ? 'SUPER' : `GM${gmInfo.gmLevel}`}
              </span>
            )}
            <span className="text-[11px] text-white/55 font-mono hidden md:block">
              {account?.id || user?.username}
            </span>
            <div className="w-7 h-7 rounded-full bg-[#00e1d7]/15 border border-[#00e1d7]/30
                            flex items-center justify-center text-[#00e1d7] font-bold text-xs">
              {(account?.id?.[0] || user?.username?.[0] || 'A').toUpperCase()}
            </div>
          </div>
        </header>

        {/* Sidebar Desktop */}
        <aside className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 bg-[#0a1015]
                          border-r border-white/[0.05] z-40 transition-[width] duration-300 ${sidebarWidth}`}>
          {renderNav(false)}
          <button
            onClick={() => setCollapsed(c => !c)}
            aria-label={collapsed ? 'Expandir' : 'Recolher'}
            className="absolute top-16 -right-3 w-6 h-6 rounded-full bg-[#161e26]
                       border border-white/[0.10] hover:border-[#00e1d7]/50
                       hover:bg-[#1d2731] hover:text-[#00e1d7]
                       text-white/70 flex items-center justify-center
                       shadow-lg transition-all cursor-pointer z-[60]"
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-14 bottom-0 w-[min(92vw,360px)] bg-[#0a1015]
                             border-r border-white/[0.05] overflow-y-auto overscroll-contain">
              {renderNav(true)}
            </aside>
          </div>
        )}

        <main className={`flex-1 z-10 relative pt-14 transition-[margin] duration-300 ${mainPadding}`}>
          <div className="p-3 sm:p-5 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </GmGate>
  );
}
